import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../../components/feature/Footer';
import TableOfContents from '../../../components/feature/TableOfContents';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

export default function HowToFindInvestorPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'what-is-investor', title: 'What Exactly Is a House Investor?' },
    { id: 'why-sell-investor', title: 'Why Would You Want to Sell to an Investor?' },
    { id: 'where-to-find', title: 'Where to Find Real Estate Investors in El Paso' },
    { id: 'vetting-investors', title: 'How to Vet Investors Before Accepting an Offer' },
    { id: 'understanding-offers', title: 'Understanding Investor Offers: What\'s Fair?' },
    { id: 'common-questions', title: 'Common Questions Sellers Ask' },
    { id: 'elpaso-market', title: 'El Paso House Investors: What Makes Our Market Unique' },
    { id: 'selling-process', title: 'The Step-by-Step Process of Selling to an Investor' },
    { id: 'red-flags', title: 'Red Flags to Watch Out For' },
    { id: 'final-tips', title: 'Final Tips for a Successful Investor Sale' }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold text-teal-600">El Paso Cash Home Buyers</span>
              </Link>
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors">Home</Link>
                <Link to="/get-cash" className="text-gray-700 hover:text-teal-600 transition-colors">Blog</Link>
                <a href="tel:915-555-0100" className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap">
                  Call (915) 555-0100
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <nav aria-label="Breadcrumb" className="flex items-center justify-center space-x-2 text-teal-100 mb-4 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/quick-sale" className="hover:text-white transition-colors">Quick Sale</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white font-medium">How to Find an Investor</span>
              </nav>
              <div className="inline-block bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                Complete Guide
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                How to Find an Investor to Buy My House in El Paso
              </h1>
              <p className="text-xl text-teal-100 mb-8 leading-relaxed">
                Your comprehensive guide to connecting with legitimate house investors, getting fair cash offers, and closing quickly in the El Paso real estate market
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-teal-100">
                <span className="flex items-center">
                  <i className="ri-time-line mr-2"></i>
                  22 min read
                </span>
                <span className="flex items-center">
                  <i className="ri-calendar-line mr-2"></i>
                  Updated January 2025
                </span>
                <span className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  El Paso, Texas
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Table of Contents - Sidebar */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <TableOfContents items={sections} />
              </div>
            </aside>

            {/* Article Content */}
            <article className="lg:col-span-3">
              <div id="what-is-investor">
                <ArticlePartOne />
              </div>
              
              <div id="where-to-find">
                <ArticlePartTwo />
              </div>
              
              <div id="vetting-investors">
                <ArticlePartThree />
              </div>
              
              <div id="elpaso-market">
                <ArticlePartFour />
              </div>

              {/* Call to Action */}
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white text-center mt-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Get a Cash Offer for Your El Paso Home?
                </h2>
                <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
                  We're local El Paso investors who buy houses in any condition. Get a fair, no-obligation cash offer within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="tel:915-555-0100" 
                    className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-50 transition-colors inline-flex items-center justify-center whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2 text-xl"></i>
                    Call (915) 555-0100
                  </a>
                  <Link 
                    to="/" 
                    className="bg-teal-800 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-900 transition-colors inline-flex items-center justify-center whitespace-nowrap"
                  >
                    Get Your Cash Offer
                    <i className="ri-arrow-right-line ml-2 text-xl"></i>
                  </Link>
                </div>
                <p className="text-teal-100 mt-6 text-sm">
                  No fees • No commissions • Close on your timeline
                </p>
              </div>

              {/* Related Articles */}
              <div className="mt-16 border-t border-gray-200 pt-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <Link to="/get-cash/sell-house-fast-el-paso" className="group">
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="p-6">
                        <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                          How to Sell Your House Fast in El Paso
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Learn proven strategies to sell your home quickly in the El Paso market.
                        </p>
                      </div>
                    </div>
                  </Link>

                  <Link to="/get-cash/sell-house-as-is-el-paso" className="group">
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="p-6">
                        <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                          Selling Your House As-Is in El Paso
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Discover how to sell your property without making any repairs or updates.
                        </p>
                      </div>
                    </div>
                  </Link>

                  <Link to="/get-cash/avoiding-foreclosure-options" className="group">
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="p-6">
                        <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                          Avoiding Foreclosure: Your Options
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Explore alternatives to foreclosure and protect your financial future.
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
